import { MantineProvider } from '@mantine/core';
import { QueryClientProvider } from '@tanstack/react-query';
import { Route, BrowserRouter as Router, Routes } from 'react-router';
import { OutOfBloom } from '../../components/out-of-bloom/out-of-bloom';
import { ProtectedRoute } from '../../components/protected-route/protected-route';
import { SessionBootstrap } from '../../components/session-bootstrap/session-bootstrap';
import { SuperuserBar } from '../../components/superuser-bar/superuser-bar';
import { Setup } from '../../containers/setup/setup';
import { BoardProvider, GlobalProvider, TeamProvider, UserProvider } from '../../contexts';
import { GlobalFilterProvider } from '../../contexts/global-filter';
import { queryClient } from '../../query-client';
import { theme } from '../../styles/theme';
import { ArtifactExportRoute } from '../artifact-export-route/artifact-export-route';
import { FloraProvider } from '../flora/flora.context';
import { NavigationContainer } from '../navigation/navigation';
import { NotificationProvider } from '../notifications';
import { Activation } from '../onboarding/activation/activation';
import { CreatePasswordForm } from '../onboarding/reset-password/create-password-form';
import { CreatePasswordSuccess } from '../onboarding/reset-password/create-password-success';
import { ResetPasswordForm } from '../onboarding/reset-password/reset-password-form';
import { ResetPasswordSuccess } from '../onboarding/reset-password/reset-password-success';
import { SignIn } from '../onboarding/sign-in/sign-in';
import { SignUp } from '../onboarding/sign-up/sign-up';
import { ProcessProvider } from '../process/context/process.context';
import { SidebarProvider } from '../side-bar/side-bar.context';

export function App() {
  return (
    <Router>
      <MantineProvider theme={theme} defaultColorScheme="light">
        <NotificationProvider>
          <QueryClientProvider client={queryClient}>
            <SessionBootstrap>
              <UserProvider>
                <GlobalProvider>
                  <GlobalFilterProvider>
                    <TeamProvider>
                      <BoardProvider>
                        <ProcessProvider>
                          <FloraProvider>
                            <SidebarProvider>
                              <Routes>
                                <Route path="/" element={<SignIn />} />
                                <Route path="/sign-up" element={<SignUp />} />
                                <Route path="/activate/:uid/:token" element={<Activation />} />
                                <Route path="/reset-password" element={<ResetPasswordForm />} />
                                <Route path="/reset-password-success" element={<ResetPasswordSuccess />} />
                                <Route
                                  path="/password/reset/confirm/:uid/:token/:email"
                                  element={<CreatePasswordForm />}
                                />
                                <Route path="/create-password-success" element={<CreatePasswordSuccess />} />
                                {/* Auth-less route used by the BE Playwright renderer to
                                 * screenshot a single Flora artifact for assembly into a PDF.
                                 * Must remain OUTSIDE <ProtectedRoute> so it renders without
                                 * an authenticated session. */}
                                <Route path="/_artifact-export/:type" element={<ArtifactExportRoute />} />
                                <Route
                                  path="/application/setup"
                                  element={
                                    <ProtectedRoute>
                                      <Setup />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/application/*"
                                  element={
                                    <ProtectedRoute>
                                      <NavigationContainer />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route path="*" element={<OutOfBloom />} />
                              </Routes>
                              <SuperuserBar />
                            </SidebarProvider>
                          </FloraProvider>
                        </ProcessProvider>
                      </BoardProvider>
                    </TeamProvider>
                  </GlobalFilterProvider>
                </GlobalProvider>
              </UserProvider>
            </SessionBootstrap>
          </QueryClientProvider>
        </NotificationProvider>
      </MantineProvider>
    </Router>
  );
}
